import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  MapIcon, 
  CurrencyRupeeIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { DashboardStats } from '../../types';
import apiService from '../../services/api';

interface StatCard {
  title: string;
  value: string;
  change?: string;
  changeType?: 'increase' | 'decrease';
  icon: React.ComponentType<any>;
  color: string;
  href?: string;
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDashboardStats();
      setStats(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    if (amount >= 10000000) { // 1 Crore
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) { // 1 Lakh
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else {
      return `₹${amount.toLocaleString()}`;
    }
  };

  const getStatCards = (): StatCard[] => {
    if (!stats) return [];

    return [
      {
        title: 'Total Layouts',
        value: stats.summary.total_layouts.toString(),
        icon: BuildingOfficeIcon,
        color: 'bg-blue-500',
        href: '/layouts'
      },
      {
        title: 'Total Plots',
        value: stats.summary.total_plots.toString(),
        icon: MapIcon,
        color: 'bg-green-500',
        href: '/plots'
      },
      {
        title: 'Available Plots',
        value: stats.summary.available_plots.toString(),
        icon: MapIcon,
        color: 'bg-yellow-500',
        href: '/plots?status=available'
      },
      {
        title: 'Sold Plots',
        value: stats.summary.sold_plots.toString(),
        icon: ChartBarIcon,
        color: 'bg-purple-500',
        href: '/plots?status=sold'
      },
      {
        title: 'Total Revenue',
        value: formatCurrency(stats.summary.total_inventory_value),
        icon: CurrencyRupeeIcon,
        color: 'bg-indigo-500',
        href: '/reports/revenue'
      },
      {
        title: 'Collected Amount',
        value: formatCurrency(stats.summary.total_collected),
        icon: CurrencyRupeeIcon,
        color: 'bg-emerald-500'
      },
      {
        title: 'Pending Amount',
        value: formatCurrency(stats.summary.total_pending),
        icon: ExclamationTriangleIcon,
        color: 'bg-red-500'
      },
      {
        title: 'Token Amount',
        value: formatCurrency(stats.summary.total_tokens),
        icon: CurrencyRupeeIcon,
        color: 'bg-orange-500'
      }
    ];
  };

  const getStatusColor = (statusName: string): string => {
    const colorMap: { [key: string]: string } = {
      'Open': 'bg-green-100 text-green-800',
      'Hold': 'bg-yellow-100 text-yellow-800',
      'Reserved': 'bg-blue-100 text-blue-800',
      'Token': 'bg-purple-100 text-purple-800',
      'Agreement': 'bg-indigo-100 text-indigo-800',
      'Registered': 'bg-gray-100 text-gray-800'
    };
    return colorMap[statusName] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="card p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        </div>
        <div className="card p-6">
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadDashboardData} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statCards = getStatCards();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-3 sm:pb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">Welcome to your Plot Management dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const CardContent = (
            <div className="card p-4 sm:p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{stat.title}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  {stat.change && (
                    <div className={`flex items-center mt-2 text-sm ${
                      stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.changeType === 'increase' ? (
                        <ArrowUpIcon className="h-4 w-4 mr-1" />
                      ) : (
                        <ArrowDownIcon className="h-4 w-4 mr-1" />
                      )}
                      {stat.change}
                    </div>
                  )}
                </div>
                <div className={`p-2 sm:p-3 rounded-full ${stat.color} flex-shrink-0`}>
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
            </div>
          );

          return stat.href ? (
            <Link key={index} to={stat.href}>
              {CardContent}
            </Link>
          ) : (
            <div key={index}>
              {CardContent}
            </div>
          );
        })}
      </div>

      {/* Charts and Additional Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Status Distribution */}
        <div className="card p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Plot Status Distribution</h2>
          <div className="space-y-3 sm:space-y-4">
            {stats?.statusDistribution.map((status) => (
              <div key={status.id} className="flex items-center justify-between gap-2">
                <div className="flex items-center min-w-0">
                  <div 
                    className="w-3 h-3 sm:w-4 sm:h-4 rounded mr-2 sm:mr-3 flex-shrink-0"
                    style={{ backgroundColor: status.status_color }}
                  ></div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                    {status.status_name}
                  </span>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                  <span className="text-xs sm:text-sm text-gray-600">
                    {status.plot_count}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">
                    {formatCurrency(status.total_value)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Recent Activities</h2>
            <Link to="/reports" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          <div className="space-y-4">
            {stats?.recentActivities.slice(0, 5).map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 pb-3 border-b border-gray-100 last:border-b-0">
                <div 
                  className="w-2 h-2 rounded-full mt-2"
                  style={{ backgroundColor: activity.status_color }}
                ></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    Plot <span className="font-medium">{activity.plot_number}</span> in{' '}
                    <span className="font-medium">{activity.layout_name}</span> status updated to{' '}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.new_status)}`}>
                      {activity.new_status}
                    </span>
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      by {activity.updated_by_name || 'System'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly Trend */}
      {stats?.monthlyTrend && stats.monthlyTrend.length > 0 && (
        <div className="card p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Monthly Trend</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {stats.monthlyTrend.map((trend, index) => (
              <div key={index} className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{trend.month}</p>
                <p className="text-base sm:text-lg font-bold text-gray-900">{trend.transactions}</p>
                <p className="text-xs text-gray-500 truncate">{formatCurrency(trend.revenue)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Link to="/layouts/new" className="btn-primary text-center py-3">
            Create New Layout
          </Link>
          <Link to="/plots" className="btn-secondary text-center py-3">
            Manage Plots
          </Link>
          <Link to="/reports" className="btn-outline text-center py-3">
            Generate Reports
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 